import type { CustomNode } from '../types';

/**
 * Builds an IO NodeMetadata object (the shape used by Flow Nodes → Metadata)
 * from a custom node. If the node already carries a real pasted NodeMetadata,
 * that is preferred (with the domain/server-assigned fields stripped so it's a
 * clean, importable object); otherwise one is generated from the stored fields.
 */
export function buildNodeMetadata(node: CustomNode): Record<string, unknown> {
  if (node.metadata && typeof node.metadata === 'object') {
    const raw = node.metadata as Record<string, unknown>;
    const inner =
      raw.node && typeof raw.node === 'object' ? (raw.node as Record<string, unknown>) : raw;
    // Drop fields IO assigns on import so the file is a clean template.
    const { domainId: _domainId, meta: _meta, id: _id, ...clean } = inner;
    void _domainId;
    void _meta;
    void _id;
    return clean;
  }

  return {
    name: node.name || node.bundleName,
    active: true,
    bundleName: node.bundleName,
    bundleVersion: node.bundleVersion,
    nodeName: node.nodeName || '',
    deletable: true,
    deprecated: false,
    displayName: [],
    description: [],
    category: node.category || '',
    icon: node.iconDataUrl || '',
    outputs: (node.outputs || [])
      // Drop catalog placeholders for dynamic outputs (e.g. "user-defined (via callback)");
      // those are added in IO and covered by allowAdditionalOutputs.
      .filter((output) => !/^user-defined/i.test(output.name))
      .map((output) => ({ name: output.name, level: output.level || 'warning' })),
    inputs:
      node.inputs && node.inputs.length > 0
        ? node.inputs.map((input) => ({ name: input.name }))
        : [{ name: 'input' }],
    sections: [],
    allowAdditionalOutputs: Boolean(node.allowAdditionalOutputs)
  };
}

export function metadataFileName(node: CustomNode): string {
  return `${node.bundleName}-${node.bundleVersion}.metadata.json`;
}
